import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { RichTextEditor } from '../ui/rich-text-editor';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { ConfirmDialog } from '../ui/confirm-dialog';
import { Tabs, TabsContent } from '../ui/tabs';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Menubar,
  MenubarContent,
  MenubarItem,
  MenubarMenu,
  MenubarSeparator,
  MenubarShortcut,
  MenubarTrigger,
} from "@/components/ui/menubar";
import {
    Plus,
    Edit,
    Trash2,
    Calendar,
    Save,
    Search,
    Users,
    DollarSign,
    Clock,
    CreditCard,
    Upload,
    Building2,
    CheckCircle,
    XCircle,
    Eye
} from 'lucide-react';
import { toast } from 'sonner';
import { trainingService } from '@/services/trainingService';
import { normalizeMediaUrl } from '@/utils/media';
import { useApp } from '@/pages/providers/AppProvider';
import { apiClient } from '@/lib/api-client';

export function TrainingManagement() {
    const { siteSettings, setSiteSettings } = useApp();
    const [trainings, setTrainings] = useState<any[]>([]);
    const [enrollments, setEnrollments] = useState<any[]>([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [showCreateForm, setShowCreateForm] = useState(false);
    const [editingTraining, setEditingTraining] = useState<any>(null);
    const [deleteDialog, setDeleteDialog] = useState<{ open: boolean; trainingId: string | null; trainingTitle: string }>({ open: false, trainingId: null, trainingTitle: '' });
    const [isLoading, setIsLoading] = useState(true);
    const [activeManagementTab, setActiveManagementTab] = useState('trainings');

    const [paymentSettings, setPaymentSettings] = useState({
        trainingWhatsAppNumber: siteSettings.trainingWhatsAppNumber || '',
        trainingQrCodeUrl: siteSettings.trainingQrCodeUrl || ''
    });

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        setIsLoading(true);
        try {
            const [trainingRes, enrollmentRes] = await Promise.all([
                trainingService.getAll(),
                trainingService.getAllEnrollments()
            ]);

            if (Array.isArray(trainingRes)) {
                setTrainings(trainingRes);
            }
            if (Array.isArray(enrollmentRes)) {
                setEnrollments(enrollmentRes);
            }
        } catch (err) {
            console.error('Failed to load training data:', err);
            toast.error('Failed to load training management data');
        } finally {
            setIsLoading(false);
        }
    };

    const loadTrainings = async () => {
        try {
            const res = await trainingService.getAll();
            if (Array.isArray(res)) {
                setTrainings(res);
            }
        } catch (err) {
            console.error('Failed to load trainings:', err);
        }
    };

    const handleSaveSettings = async () => {
        try {
            // site_info section manages these settings
            const updatedSettings = {
                ...siteSettings,
                trainingWhatsAppNumber: paymentSettings.trainingWhatsAppNumber,
                trainingQrCodeUrl: paymentSettings.trainingQrCodeUrl
            };

            await apiClient.saveSectionSettings('site_info', updatedSettings, true);
            setSiteSettings(updatedSettings);
            toast.success('Training payment settings updated successfully');
        } catch (err) {
            console.error('Failed to save settings:', err);
            toast.error('Failed to update payment settings');
        }
    };

    const handleQrUpload = async (file: File) => {
        if (!file) return;
        try {
            const res = await apiClient.uploadSectionImage('site_info', file);
            const url = res.url || res.location || res.path;
            if (url) {
                const normalized = normalizeMediaUrl(url);
                setPaymentSettings(prev => ({ ...prev, trainingQrCodeUrl: normalized }));
                toast.success('QR Code uploaded. Don\'t forget to save changes.');
            }
        } catch (err) {
            toast.error('Failed to upload QR code');
        }
    };

    const [newTraining, setNewTraining] = useState({
        title: '',
        instructor: '',
        startDate: '',
        duration: '',
        price: '',
        description: '',
        status: 'upcoming',
        taxPolicy: 'including',
        vatPercentage: '13',
        image: null as File | null,
        imageUrl: ''
    });

    const handleCreateTraining = async () => {
        if (!newTraining.title.trim() || !newTraining.instructor.trim() || !newTraining.startDate || !newTraining.price.trim()) {
            toast.error('Please fill in all required fields');
            return;
        }

        const payload: any = {
            title: newTraining.title,
            instructor: newTraining.instructor,
            startDate: new Date(newTraining.startDate),
            duration: newTraining.duration,
            price: newTraining.price,
            description: newTraining.description,
            status: newTraining.status,
            taxPolicy: newTraining.taxPolicy,
            vatPercentage: newTraining.taxPolicy === 'excluding' ? newTraining.vatPercentage : '0',
        };

        if (newTraining.image) {
            payload.image = newTraining.image;
        }

        try {
            await trainingService.create(payload);
            toast.success('Training created successfully');
            setShowCreateForm(false);
            setNewTraining({
                title: '',
                instructor: '',
                startDate: '',
                duration: '',
                price: '',
                description: '',
                status: 'upcoming',
                taxPolicy: 'including',
                vatPercentage: '13',
                image: null,
                imageUrl: ''
            });
            loadTrainings();
        } catch (err: any) {
            console.error('Create training failed:', err);
            toast.error(err.message || 'Failed to create training');
        }
    };

    const handleUpdateTraining = async () => {
        if (!editingTraining) return;

        const payload: any = {
            title: editingTraining.title,
            instructor: editingTraining.instructor,
            startDate: new Date(editingTraining.startDate),
            duration: editingTraining.duration,
            price: editingTraining.price,
            description: editingTraining.description,
            status: editingTraining.status,
            taxPolicy: editingTraining.taxPolicy || 'including',
            vatPercentage: editingTraining.taxPolicy === 'excluding' ? (editingTraining.vatPercentage || '0') : '0',
        };

        if (editingTraining.newImage) {
            payload.image = editingTraining.newImage;
        }

        try {
            await trainingService.update(editingTraining._id || editingTraining.id, payload);
            toast.success('Training updated successfully');
            setEditingTraining(null);
            loadTrainings();
        } catch (err: any) {
            console.error('Update training failed:', err);
            toast.error(err.message || 'Failed to update training');
        }
    };

    const handleDeleteTraining = async (id: string) => {
        try {
            await trainingService.delete(id);
            toast.success('Training deleted successfully');
            loadTrainings();
        } catch (err: any) {
            console.error('Delete training failed:', err);
            toast.error(err.message || 'Failed to delete training');
        }
    };

    const filteredTrainings = trainings.filter(training => {
        const term = searchTerm.toLowerCase();
        const matchesSearch =
            training.title?.toLowerCase().includes(term) ||
            training.instructor?.toLowerCase().includes(term);
        const matchesStatus = statusFilter === 'all' || training.status === statusFilter;
        return matchesSearch && matchesStatus;
    });

    const renderTrainingForm = (
        data: any,
        setData: (data: any) => void,
        submitAction: () => void,
        submitLabel: string
    ) => (
        <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                    <Label htmlFor="title">Title *</Label>
                    <Input id="title" value={data.title} onChange={(e) => setData({ ...data, title: e.target.value })} />
                </div>
                <div className="space-y-2">
                    <Label htmlFor="instructor">Instructor *</Label>
                    <Input id="instructor" value={data.instructor} onChange={(e) => setData({ ...data, instructor: e.target.value })} />
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                    <Label htmlFor="startDate">Start Date *</Label>
                    <Input
                        id="startDate"
                        type="date"
                        value={data.startDate ? new Date(data.startDate).toISOString().split('T')[0] : ''}
                        onChange={(e) => setData({ ...data, startDate: e.target.value })}
                    />
                </div>
                <div className="space-y-2">
                    <Label htmlFor="duration">Duration *</Label>
                    <Input id="duration" placeholder="e.g. 4 Weeks" value={data.duration} onChange={(e) => setData({ ...data, duration: e.target.value })} />
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                    <Label htmlFor="price">Price *</Label>
                    <Input id="price" placeholder="e.g. NPR 15,000" value={data.price} onChange={(e) => setData({ ...data, price: e.target.value })} />
                </div>
                <div className="space-y-2">
                    <Label htmlFor="status">Status</Label>
                    <Select value={data.status} onValueChange={(value) => setData({ ...data, status: value })}>
                        <SelectTrigger>
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="upcoming">Upcoming</SelectItem>
                            <SelectItem value="active">Active</SelectItem>
                            <SelectItem value="completed">Completed</SelectItem>
                            <SelectItem value="inactive">Inactive</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                    <Label htmlFor="taxPolicy">Tax Policy</Label>
                    <Select value={data.taxPolicy || 'including'} onValueChange={(value) => setData({ ...data, taxPolicy: value })}>
                        <SelectTrigger>
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="including">Including Tax</SelectItem>
                            <SelectItem value="excluding">Excluding Tax</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
                {data.taxPolicy === 'excluding' && (
                    <div className="space-y-2">
                        <Label htmlFor="vatPercentage">VAT Percentage (%)</Label>
                        <Input
                            id="vatPercentage"
                            type="number"
                            placeholder="e.g. 13"
                            value={data.vatPercentage || ''}
                            onChange={(e) => setData({ ...data, vatPercentage: e.target.value })}
                        />
                    </div>
                )}
            </div>

            <div className="space-y-2">
                <Label htmlFor="image">Image</Label>
                <Input
                    id="image"
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                            if (submitLabel === 'Create Training') {
                                setData({ ...data, image: file, imageUrl: URL.createObjectURL(file) });
                            } else {
                                setData({ ...data, newImage: file, newImageUrl: URL.createObjectURL(file) });
                            }
                        }
                    }}
                />
                {(data.imageUrl || data.image || data.newImageUrl) && (
                    <div className="mt-2">
                        <img
                            src={data.newImageUrl || data.imageUrl || normalizeMediaUrl(data.image)}
                            alt="Preview"
                            className="max-h-40 rounded object-cover"
                        />
                    </div>
                )}
            </div>

            <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <RichTextEditor
                    value={data.description}
                    onChange={(value) => setData({ ...data, description: value })}
                    placeholder="Detailed description of the training..."
                />
            </div>

            <div className="flex justify-end pt-4">
                <Button onClick={submitAction}>{submitLabel}</Button>
            </div>
        </div>
    );

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <div>
                    <h2 className="text-2xl font-bold">Training Management</h2>
                    <p className="text-gray-600">Manage programs, enrollments, and payments</p>
                </div>
            </div>

            <div className="flex justify-center mb-8">
                <Menubar className="h-12 bg-white/80 backdrop-blur-md border border-gray-200/50 rounded-full p-1 shadow-lg shadow-gray-200/20 relative w-full">
                    {[
                        { id: 'trainings', label: 'Programs', icon: Calendar },
                        { id: 'enrollments', label: 'Enrollments', icon: Users },
                        { id: 'settings', label: 'Settings', icon: CreditCard },
                    ].map((tab) => (
                        <MenubarMenu key={tab.id} className="flex-1">
                            <MenubarTrigger
                                className={`relative z-10 rounded-full px-8 py-2.5 flex items-center justify-center gap-2 transition-colors duration-300 cursor-pointer w-full ${
                                    activeManagementTab === tab.id ? 'text-white font-semibold' : 'text-slate-600 hover:text-slate-900'
                                }`}
                                onClick={() => setActiveManagementTab(tab.id)}
                            >
                                {activeManagementTab === tab.id && (
                                    <motion.div
                                        layoutId="activeTrainingTab"
                                        className="absolute inset-0 bg-[#e37300] rounded-full shadow-md z-[-1]"
                                        transition={{ type: "spring", bounce: 0.2, duration: 0.6 }}
                                    />
                                )}
                                <tab.icon className={`h-4 w-4 ${activeManagementTab === tab.id ? 'text-white' : ''}`} />
                                <span>{tab.label}</span>
                            </MenubarTrigger>
                        </MenubarMenu>
                    ))}
                </Menubar>
            </div>

            <Tabs value={activeManagementTab} onValueChange={setActiveManagementTab} className="space-y-6">

                <TabsContent value="trainings">
                    <Card>
                        <CardContent className="pt-6">
                            <div className="flex flex-col sm:flex-row gap-4 mb-6">
                                <div className="flex-1 relative">
                                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                                    <Input
                                        placeholder="Search trainings..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        className="pl-10"
                                    />
                                </div>
                                <div className="w-[200px]">
                                    <Select value={statusFilter} onValueChange={setStatusFilter}>
                                        <SelectTrigger>
                                            <SelectValue placeholder="Filter Status" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="all">All Status</SelectItem>
                                            <SelectItem value="upcoming">Upcoming</SelectItem>
                                            <SelectItem value="active">Active</SelectItem>
                                            <SelectItem value="completed">Completed</SelectItem>
                                            <SelectItem value="inactive">Inactive</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                                <Button onClick={() => setShowCreateForm(true)} className="flex items-center gap-2">
                                    <Plus className="h-4 w-4" /> New Training
                                </Button>
                            </div>

                            {showCreateForm && (
                                <Card className="mb-6 border-blue-200 bg-blue-50/30">
                                    <CardHeader className="flex flex-row items-center justify-between pb-2">
                                        <CardTitle>Create New Training</CardTitle>
                                        <Button variant="ghost" size="sm" onClick={() => setShowCreateForm(false)}>Cancel</Button>
                                    </CardHeader>
                                    <CardContent>
                                        {renderTrainingForm(newTraining, setNewTraining, handleCreateTraining, 'Create Training')}
                                    </CardContent>
                                </Card>
                            )}

                            {editingTraining && (
                                <Card className="mb-6 border-amber-200 bg-amber-50/30">
                                    <CardHeader className="flex flex-row items-center justify-between pb-2">
                                        <CardTitle>Edit Training</CardTitle>
                                        <Button variant="ghost" size="sm" onClick={() => setEditingTraining(null)}>Cancel</Button>
                                    </CardHeader>
                                    <CardContent>
                                        {renderTrainingForm(editingTraining, setEditingTraining, handleUpdateTraining, 'Update Training')}
                                    </CardContent>
                                </Card>
                            )}

                            <div className="space-y-4">
                                {isLoading ? (
                                    <div className="text-center py-10">Loading trainings...</div>
                                ) : filteredTrainings.length === 0 ? (
                                    <div className="text-center py-10 text-gray-500">No trainings found matching your criteria.</div>
                                ) : (
                                    filteredTrainings.map((training) => (
                                        <div key={training._id || training.id} className="border rounded-lg p-4 hover:shadow-md transition-shadow bg-white">
                                            <div className="flex flex-col md:flex-row gap-4">
                                                <div className="w-full md:w-32 h-32 flex-shrink-0 bg-gray-100 rounded-md overflow-hidden">
                                                    {training.image ? (
                                                        <img
                                                            src={normalizeMediaUrl(training.image)}
                                                            alt={training.title}
                                                            className="w-full h-full object-cover"
                                                        />
                                                    ) : (
                                                        <div className="w-full h-full flex items-center justify-center text-gray-400">
                                                            <Calendar className="h-8 w-8" />
                                                        </div>
                                                    )}
                                                </div>
                                                <div className="flex-1 space-y-2">
                                                    <div className="flex justify-between items-start">
                                                        <div>
                                                            <h3 className="text-lg font-bold text-gray-900">{training.title}</h3>
                                                            <div className="flex items-center gap-4 text-sm text-gray-500 mt-1">
                                                                <span className="flex items-center gap-1"><Users className="h-3 w-3" /> {training.instructor}</span>
                                                                <span className="flex items-center gap-1"><Clock className="h-3 w-3" /> {training.duration}</span>
                                                                <span className="flex items-center gap-1"><DollarSign className="h-3 w-3" /> {training.price}</span>
                                                            </div>
                                                        </div>
                                                        <Badge className={`${training.status === 'active' ? 'bg-green-100 text-green-800' :
                                                            training.status === 'upcoming' ? 'bg-blue-100 text-blue-800' :
                                                                'bg-gray-100 text-gray-800'
                                                            }`}>
                                                            {training.status}
                                                        </Badge>
                                                    </div>

                                                    <div className="text-sm text-gray-600 line-clamp-2">
                                                        {training.description ? training.description.replace(/<[^>]+>/g, '') : ''}
                                                    </div>

                                                    <div className="flex justify-between items-center pt-2">
                                                        <div className="text-xs text-gray-500">
                                                            Start Date: {new Date(training.startDate).toLocaleDateString()}
                                                        </div>
                                                        <div className="flex gap-2">
                                                            <Button variant="outline" size="sm" onClick={() => setEditingTraining(training)}>
                                                                <Edit className="h-4 w-4 mr-1" /> Edit
                                                            </Button>
                                                            <Button
                                                                variant="destructive"
                                                                size="sm"
                                                                className="bg-red-600 hover:bg-red-700 text-white"
                                                                onClick={() => setDeleteDialog({ open: true, trainingId: training._id || training.id, trainingTitle: training.title })}
                                                            >
                                                                <Trash2 className="h-4 w-4 mr-1" /> Delete
                                                            </Button>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ))
                                )}
                            </div>
                        </CardContent>
                    </Card>
                </TabsContent>

                <TabsContent value="enrollments">
                    <Card>
                        <CardHeader>
                            <CardTitle>User Enrollments</CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="overflow-x-auto">
                                <table className="w-full">
                                    <thead>
                                        <tr className="border-b text-left">
                                            <th className="py-3 px-4">Training</th>
                                            <th className="py-3 px-4">Organization</th>
                                            <th className="py-3 px-4">Participants</th>
                                            <th className="py-3 px-4">Total Fee</th>
                                            <th className="py-3 px-4">Status</th>
                                            <th className="py-3 px-4">Date</th>
                                            <th className="py-3 px-4">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {isLoading ? (
                                            <tr><td colSpan={7} className="py-10 text-center">Loading enrollments...</td></tr>
                                        ) : enrollments.length === 0 ? (
                                            <tr><td colSpan={7} className="py-10 text-center text-gray-500">No enrollments found.</td></tr>
                                        ) : (
                                            enrollments.map((enrollment) => (
                                                <tr key={enrollment._id || enrollment.id} className="border-b hover:bg-gray-50">
                                                    <td className="py-3 px-4 font-medium">{enrollment.trainingTitle}</td>
                                                    <td className="py-3 px-4">{enrollment.organizationName}</td>
                                                    <td className="py-3 px-4">
                                                        <Badge variant="outline">{enrollment.participantCount}</Badge>
                                                    </td>
                                                    <td className="py-3 px-4">
                                                        <div className="flex flex-col">
                                                            <span className="font-bold text-blue-600">NPR {(enrollment.totalFee || 0).toLocaleString()}</span>
                                                            {enrollment.vatAmount > 0 && (
                                                                <span className="text-[10px] text-gray-500 font-medium">
                                                                    (VAT {enrollment.vatPercentage}%: +{enrollment.vatAmount.toLocaleString()})
                                                                </span>
                                                            )}
                                                        </div>
                                                    </td>
                                                    <td className="py-3 px-4">
                                                        <Badge className={
                                                            enrollment.status === 'completed' ? 'bg-green-100 text-green-800' :
                                                                enrollment.status === 'pending_payment' ? 'bg-yellow-100 text-yellow-800' :
                                                                    'bg-gray-100 text-gray-800'
                                                        }>
                                                            {enrollment.status}
                                                        </Badge>
                                                    </td>
                                                    <td className="py-3 px-4 text-xs text-gray-500">
                                                        {new Date(enrollment.created_at).toLocaleDateString()}
                                                    </td>
                                                    <td className="py-3 px-4 flex gap-2">
                                                        <Button variant="outline" size="sm">
                                                            <Eye className="h-4 w-4" />
                                                        </Button>
                                                        {enrollment.status !== 'completed' && (
                                                            <Button
                                                                variant="outline"
                                                                size="sm"
                                                                className="text-green-600 hover:bg-green-50"
                                                                onClick={async () => {
                                                                    try {
                                                                        await trainingService.updateEnrollmentStatus(enrollment._id || enrollment.id, 'completed');
                                                                        toast.success('Enrollment marked as completed');
                                                                        loadData();
                                                                    } catch (err) {
                                                                        toast.error('Failed to update status');
                                                                    }
                                                                }}
                                                            >
                                                                Mark Paid
                                                            </Button>
                                                        )}
                                                    </td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </CardContent>
                    </Card>
                </TabsContent>

                <TabsContent value="settings">
                    <Card>
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                                <CreditCard className="h-5 w-5" />
                                Payment Configuration
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-6">
                            <div className="space-y-4">
                                <div className="space-y-2">
                                    <Label htmlFor="whatsapp">WhatsApp Number for Confirmation</Label>
                                    <Input
                                        id="whatsapp"
                                        value={paymentSettings.trainingWhatsAppNumber}
                                        onChange={(e) => setPaymentSettings(prev => ({ ...prev, trainingWhatsAppNumber: e.target.value }))}
                                        placeholder="+977-9800000000"
                                    />
                                    <p className="text-xs text-gray-500">Number where users send their payment screenshots.</p>
                                </div>

                                <div className="space-y-2">
                                    <Label>Payment QR Code</Label>
                                    <div className="border-2 border-dashed border-gray-300 p-6 text-center rounded-lg bg-gray-50">
                                        {paymentSettings.trainingQrCodeUrl ? (
                                            <div className="space-y-4">
                                                <img
                                                    src={normalizeMediaUrl(paymentSettings.trainingQrCodeUrl)}
                                                    alt="Payment QR"
                                                    className="max-h-64 mx-auto object-contain shadow-md rounded-md bg-white p-2"
                                                />
                                                <div className="flex justify-center gap-2">
                                                    <Button variant="outline" size="sm" onClick={() => document.getElementById('qr-input')?.click()}>
                                                        Replace QR
                                                    </Button>
                                                    <Button variant="ghost" size="sm" className="text-red-600 hove:bg-red-50" onClick={() => setPaymentSettings(prev => ({ ...prev, trainingQrCodeUrl: '' }))}>
                                                        Remove
                                                    </Button>
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="py-8">
                                                <Upload className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                                                <Button variant="outline" size="sm" onClick={() => document.getElementById('qr-input')?.click()}>
                                                    Upload QR Code
                                                </Button>
                                            </div>
                                        )}
                                        <input
                                            id="qr-input"
                                            type="file"
                                            accept="image/*"
                                            className="hidden"
                                            onChange={(e) => e.target.files?.[0] && handleQrUpload(e.target.files[0])}
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="flex justify-end pt-4 border-t">
                                <Button onClick={handleSaveSettings} className="flex items-center gap-2">
                                    <Save className="h-4 w-4" /> Save Settings
                                </Button>
                            </div>
                        </CardContent>
                    </Card>
                </TabsContent>
            </Tabs>

            <ConfirmDialog
                open={deleteDialog.open}
                onOpenChange={(open) => setDeleteDialog((prev) => ({ ...prev, open }))}
                onConfirm={async () => {
                    if (deleteDialog.trainingId) {
                        await handleDeleteTraining(deleteDialog.trainingId);
                        setDeleteDialog(prev => ({ ...prev, open: false }));
                    }
                }}
                title="Delete Training"
                description={`Are you sure you want to delete "${deleteDialog.trainingTitle}"? This action cannot be undone.`}
                confirmText="Delete"
                cancelText="Cancel"
                type="danger"
            />
        </div>
    );
}
